import React, { useEffect, useState } from "react";
import { ProjectResponseDto } from "../common/ObjectTypes";
import ProjectsList from "../projects/ProjectsList";
import { getCountries } from "../../api/organizationsApi";
import { useProfile } from "../common/ProfileProvider";
import tailwindClassnames from "../common/TailwindClassnames";
import { useNavigate } from "react-router-dom";
import i18n from "../../i18n";
import { useTranslation } from "react-i18next";
import { pageSize } from "../../api/apiUtils";
import { ProjectSearchQueryType } from "../common/QueryTypes";
import { getProjects } from "../../api/projectsApi";
import SearchBarProjects from "../projects/SearchBarProjects";
import MigrateFromWildlifeInsights from "../migrations/MigrateFromWildlifeInsights";

type OrganizationProjectsTabProps = {
  orgId: number;
  orgGuid: string;
  perms: any[];
};

const OrganizationProjectsTab: React.FC<OrganizationProjectsTabProps> = ({
  orgId,
  orgGuid,
  perms,
}) => {
  const [loading, setLoading] = useState(true);
  const [resultsLoaded, setResultsLoaded] = useState(false);
  const initialProjectsSearchQuery = {
    skip: 0,
    take: pageSize,
    sort_by: "project_name",
    sort_direction: "asc",
    org_id: orgId,
  };

  const [projectsSearchQuery, setProjectsSearchQuery] =
    useState<ProjectSearchQueryType>(initialProjectsSearchQuery);
  const [projects, setProjects] = useState<ProjectResponseDto[]>([]);
  const [totalProjects, setTotalProjects] = useState(0);
  const [countries, setCountries] = useState<any[]>([]);
  // AKL-747. Organisation-scoped rather than project-scoped: a Wildlife Insights bundle
  // can describe several projects, so what it creates belongs to an organisation.
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [hasProjects, setHasProjects] = useState(false);
  const [canAddProject, setCanAddProject] = useState(false);
  const { profile, profileLoading } = useProfile();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tempProjectsSearchQuery, setTempProjectsSearchQuery] =
    useState<ProjectSearchQueryType>(initialProjectsSearchQuery);

  useEffect(() => {
    if (!profileLoading && profile) {
      const temp = profile?.project_roles.length > 0 || profile?.is_admin;
      setHasProjects(temp);
      const isAdmin = profile?.is_admin;
      const hasPermissionsToAddProject = perms.some((perm: any) =>
        perm.name.includes("CREATE_PROJECT_IN_ORG"),
      );
      setCanAddProject(isAdmin || hasPermissionsToAddProject);
    }
  }, [profileLoading, profile, perms]);

  // Load countries when language changes
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const temp = await getCountries(i18n.language);
        setCountries(temp);
      } catch (error) {
        console.error(t("Error loading countries:"), error);
      }
    };

    loadCountries();
  }, [t]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const temp = await getProjects(projectsSearchQuery);
        setProjects(temp.projects);
        setTotalProjects(temp.count);
        setResultsLoaded(true);
      } catch (err) {
        console.error("Error loading projects:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId, projectsSearchQuery]);

  const onAddProject = () => {
    if (!orgGuid) {
      console.error("Organization GUID is missing");
      return;
    }
    navigate(`/organizations/${orgGuid}/addProject`);
  };

  const handlePageClick = (e: any) => {
    const selectedPage = e.selected;
    const skip = Math.ceil(
      selectedPage * (projectsSearchQuery.take ?? pageSize),
    );
    setProjectsSearchQuery((prevQuery) => ({
      ...prevQuery,
      skip: skip,
    }));
  };

  const handleChangeSearchQuery = (e: any) => {
    if (e) {
      let name: string;
      let value: string | boolean;
      name = e.target.name;
      value = e.target.value;
      setTempProjectsSearchQuery((prevQuery) => ({
        ...prevQuery,
        [name]: value,
      }));
    } else {
      setTempProjectsSearchQuery((prevQuery) => ({
        ...prevQuery,
        country_id: undefined,
      }));
    }
  };

  const handleSearch = async () => {
    setProjectsSearchQuery(tempProjectsSearchQuery);
  };

  /**
   * Re-fetch after a migration, which may have created several projects.
   *
   * A functional update, not a spread of the captured value: `onApplied` fires long after
   * this closure was created, and the user may have changed the filters while the
   * migration ran. Spreading the stale value would silently revert them.
   */
  const refreshProjects = () => {
    setProjectsSearchQuery((current) => ({ ...current, skip: 0 }));
  };

  return (
    <div className="flex flex-row w-full">
      <div className="overflow-x-auto w-full mx-8 mt-8">
        {/* SearchBarProjects + ProjectsList are card-interior components —
            they require this tableCard wrapper (see their JSDoc). The create
            action sits inside the toolbar here, as it does on the Users tab. */}
        {canAddProject && (
          <div className="mb-4 flex justify-end">
            <button
              type="button"
              className={tailwindClassnames.btnSecondary}
              onClick={() => setMigrationOpen(true)}
            >
              {t("Migrate from Wildlife Insights")}
            </button>
          </div>
        )}

        {canAddProject && (
          <MigrateFromWildlifeInsights
            isOpen={migrationOpen}
            onClose={() => setMigrationOpen(false)}
            // No project: this is the create path, which is the whole point of an
            // organisation-level entry point.
            projectId={null}
            orgId={orgId}
            organizations={[{ org_id: orgId }]}
            onApplied={refreshProjects}
          />
        )}

        <div className={tailwindClassnames.tableCard}>
          <SearchBarProjects
            countries={countries}
            onChangeSearchQuery={handleChangeSearchQuery}
            onSearch={handleSearch}
            tempProjectsSearchQuery={tempProjectsSearchQuery}
            orgProjectsTab={true}
            onAddProject={canAddProject ? onAddProject : undefined}
          />
          <ProjectsList
            projects={projects}
            totalCount={totalProjects}
            take={pageSize}
            offset={projectsSearchQuery.skip}
            loading={loading}
            countries={countries}
            hasProjects={hasProjects}
            resultsLoaded={resultsLoaded}
            showOrganizationsColumn={false}
            onPageClick={handlePageClick}
          />
        </div>
      </div>
    </div>
  );
};

export default OrganizationProjectsTab;
